import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import { useTranslation } from "react-i18next";
import { FAQ_WEEKDAYS, toTimeInputValue } from "../../utils/faqSkillConfig";
import { translateSkillForm } from "../../utils/skillLifecycle";
import {
  SDR_DEFAULT_MESSAGES,
  SDR_PRESET_METHODS,
  VALIDACAO_RESPOSTA_LABEL_KEYS,
  VALIDACAO_RESPOSTA_NONE,
  VALIDACAO_RESPOSTA_NUMERO_RANGE,
  VALIDACAO_RESPOSTA_TYPES,
  calendarDisplayLabel,
  normalizeSdrFields,
  parsePositiveIdList,
  resolveSdrQualificationMethod,
  sdrFieldsTotalWeight,
  slugifySdrFieldKey,
  userDisplayLabel,
} from "../../utils/sdrSkillConfig";
import { useUserAgendaCalendars } from "../../hooks/useUserAgenda";
import { useUsers } from "../../hooks/useUsers";

function ConfigAccordion({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        "&:before": { display: "none" },
        borderRadius: "12px !important",
        overflow: "hidden",
        borderColor: "divider",
        "&.Mui-expanded": { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Box sx={{ pr: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

function SubSection({ title, description, children, icon: Icon }) {
  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50"),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: description ? 0.5 : 1.5 }}>
        {Icon ? <Icon fontSize="small" color="action" sx={{ mt: 0.25 }} /> : null}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Box>
  );
}

export default function SdrSkillConfigSection({ skillConfig = {}, mergeSkillConfig, readOnly = false }) {
  const { t } = useTranslation("settings");
  const formCopy = (suffix, fallbackKey) => translateSkillForm(t, "sdr", suffix, fallbackKey);
  const mergeSdrConfig = React.useCallback(
    (partial) => mergeSkillConfig?.("sdr", partial),
    [mergeSkillConfig]
  );

  const {
    data: agendaCalendarsData,
    isLoading: agendaCalendarsLoading,
    isError: agendaCalendarsError,
  } = useUserAgendaCalendars({ enabled: true });
  const agendaCalendars = Array.isArray(agendaCalendarsData?.calendars) ? agendaCalendarsData.calendars : [];

  const {
    data: sdrUsersData,
    isLoading: sdrUsersLoading,
    isError: sdrUsersError,
  } = useUsers(1, 200, "", "humanos", { enabled: true });
  const sdrUsers = sdrUsersData?.users ?? [];

  const sdrFields = React.useMemo(() => normalizeSdrFields(skillConfig), [skillConfig]);
  const totalWeight = React.useMemo(() => sdrFieldsTotalWeight(sdrFields), [sdrFields]);
  const methodState = React.useMemo(
    () => resolveSdrQualificationMethod(skillConfig.qualification_method),
    [skillConfig.qualification_method]
  );
  const meetingEnabled = skillConfig.meeting_enabled !== false;
  const minimumScore = Math.max(0, Math.min(100, Number(skillConfig.minimum_score ?? 70) || 0));

  const sdrUsersById = React.useMemo(
    () => new Map(sdrUsers.map((user) => [Number(user.id), user])),
    [sdrUsers]
  );
  const sdrSellerIds = React.useMemo(
    () => parsePositiveIdList(skillConfig.seller_user_ids),
    [skillConfig.seller_user_ids]
  );
  const sdrCalendarIds = React.useMemo(
    () => parsePositiveIdList(skillConfig.calendar_ids),
    [skillConfig.calendar_ids]
  );
  const sdrSellerOptions = React.useMemo(() => {
    const options = [...sdrUsers];
    const seen = new Set(options.map((user) => Number(user.id)));
    for (const id of sdrSellerIds) {
      if (!seen.has(id)) {
        options.push({ id, nome: t("agentsListPage.skillParamsSdrSellerUnknown", { id }) });
      }
    }
    return options.sort((a, b) => userDisplayLabel(a).localeCompare(userDisplayLabel(b), "pt-BR"));
  }, [sdrUsers, sdrSellerIds, t]);
  const sdrCalendarOptions = React.useMemo(() => {
    const creatable = agendaCalendars.filter((cal) => cal.canCreate || cal.canManage);
    const options = [...creatable];
    const seen = new Set(options.map((cal) => Number(cal.id)));
    for (const id of sdrCalendarIds) {
      if (!seen.has(id)) {
        options.push({
          id,
          name: t("agentsListPage.skillParamsSdrCalendarUnknown", { id }),
          type: "",
        });
      }
    }
    return options.sort((a, b) =>
      calendarDisplayLabel(a, sdrUsersById).localeCompare(calendarDisplayLabel(b, sdrUsersById), "pt-BR")
    );
  }, [agendaCalendars, sdrCalendarIds, sdrUsersById, t]);
  const selectedSdrSellers = React.useMemo(
    () => sdrSellerOptions.filter((user) => sdrSellerIds.includes(Number(user.id))),
    [sdrSellerOptions, sdrSellerIds]
  );
  const selectedSdrCalendars = React.useMemo(
    () => sdrCalendarOptions.filter((cal) => sdrCalendarIds.includes(Number(cal.id))),
    [sdrCalendarOptions, sdrCalendarIds]
  );

  const sdrBusinessWeekdays = React.useMemo(() => {
    const raw = skillConfig.business_hours?.weekdays;
    if (Array.isArray(raw) && raw.length) {
      return raw.map(Number).filter((n) => Number.isInteger(n) && n >= 1 && n <= 7);
    }
    return [1, 2, 3, 4, 5];
  }, [skillConfig.business_hours?.weekdays]);

  const mergeSdrBusinessHours = React.useCallback(
    (partial) => {
      const current =
        skillConfig.business_hours && typeof skillConfig.business_hours === "object"
          ? skillConfig.business_hours
          : {};
      mergeSdrConfig({ business_hours: { ...current, ...partial } });
    },
    [mergeSdrConfig, skillConfig.business_hours]
  );

  const mergeSdrField = React.useCallback(
    (idx, partial) => {
      const nextFields = sdrFields.map((field, fieldIdx) => (fieldIdx === idx ? { ...field, ...partial } : field));
      mergeSdrConfig({ qualification_fields: nextFields });
    },
    [mergeSdrConfig, sdrFields]
  );

  const toggleSdrWeekday = React.useCallback(
    (day) => {
      const set = new Set(sdrBusinessWeekdays);
      if (set.has(day)) set.delete(day);
      else set.add(day);
      mergeSdrBusinessHours({ weekdays: [...set].sort((a, b) => a - b) });
    },
    [mergeSdrBusinessHours, sdrBusinessWeekdays]
  );

  const addSdrField = () => {
    const nextIdx = sdrFields.length + 1;
    mergeSdrConfig({
      qualification_fields: [
        ...sdrFields,
        {
          key: `campo_${nextIdx}`,
          label: "",
          question: "",
          required: true,
          weight: 10,
          validacao_resposta: VALIDACAO_RESPOSTA_NONE,
          validacao_numero_min: null,
          validacao_numero_max: null,
        },
      ],
    });
  };

  const removeSdrField = (idx) => {
    if (sdrFields.length <= 1) return;
    mergeSdrConfig({ qualification_fields: sdrFields.filter((_, fieldIdx) => fieldIdx !== idx) });
  };

  const handleMethodPresetChange = (preset) => {
    if (preset === "custom") {
      mergeSdrConfig({ qualification_method: methodState.custom || "" });
      return;
    }
    mergeSdrConfig({ qualification_method: preset });
  };

  return (
    <Stack spacing={1}>
      <Alert severity="info" icon={false} sx={{ py: 1, "& .MuiAlert-message": { width: "100%" } }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t("agentsListPage.skillParamsSdrIntroTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("agentsListPage.skillParamsSdrIntroBody")}
        </Typography>
      </Alert>

      <ConfigAccordion
        title={t("agentsListPage.skillParamsSdrQualificationTitle")}
        subtitle={formCopy("accordionSdrQualificationSubtitle", "agentsListPage.skillParamsSdrQualificationHint")}
        defaultExpanded
      >
        <Stack spacing={2.5}>
          <SubSection
            icon={FilterListOutlinedIcon}
            title={t("agentsListPage.skillParamsSdrProfileTitle")}
            description={t("agentsListPage.skillParamsSdrProfileHint")}
          >
            <Stack spacing={2}>
              <TextField
                label={t("agentsListPage.skillParamsSdrIcp")}
                helperText={t("agentsListPage.skillParamsSdrIcpHelper")}
                placeholder={t("agentsListPage.skillParamsSdrIcpPlaceholder")}
                value={skillConfig.icp_description || ""}
                onChange={(e) => mergeSdrConfig({ icp_description: e.target.value })}
                multiline
                minRows={3}
                fullWidth
                size="small"
                disabled={readOnly}
              />
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl fullWidth size="small" disabled={readOnly}>
                  <InputLabel>{t("agentsListPage.skillParamsSdrMethod")}</InputLabel>
                  <Select
                    label={t("agentsListPage.skillParamsSdrMethod")}
                    value={methodState.preset}
                    onChange={(e) => handleMethodPresetChange(e.target.value)}
                  >
                    {SDR_PRESET_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">{t("agentsListPage.skillParamsSdrMethodCustom")}</MenuItem>
                  </Select>
                  <FormHelperText>{t("agentsListPage.skillParamsSdrMethodHelper")}</FormHelperText>
                </FormControl>
                {methodState.preset === "custom" ? (
                  <TextField
                    label={t("agentsListPage.skillParamsSdrMethodCustomLabel")}
                    placeholder={t("agentsListPage.skillParamsSdrMethodCustomPlaceholder")}
                    value={methodState.custom}
                    onChange={(e) => mergeSdrConfig({ qualification_method: e.target.value })}
                    fullWidth
                    size="small"
                    disabled={readOnly}
                  />
                ) : null}
              </Stack>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("agentsListPage.skillParamsSdrMinimumScore")}
                  </Typography>
                  <Chip size="small" label={`${minimumScore}%`} color="primary" variant="outlined" />
                </Stack>
                <Slider
                  value={minimumScore}
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: "0" },
                    { value: 50, label: "50" },
                    { value: 100, label: "100" },
                  ]}
                  disabled={readOnly}
                  onChange={(_, value) => mergeSdrConfig({ minimum_score: value })}
                  sx={{ px: 0.5, mt: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("agentsListPage.skillParamsSdrMinimumScoreHelper")}
                </Typography>
              </Box>
            </Stack>
          </SubSection>

          <SubSection
            title={t("agentsListPage.skillParamsSdrFieldsTitle")}
            description={t("agentsListPage.skillParamsSdrFieldsHint")}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Chip
                size="small"
                label={t("agentsListPage.skillParamsSdrFieldsCount", { count: sdrFields.length })}
                variant="outlined"
              />
              <Chip
                size="small"
                color={totalWeight === 100 ? "success" : "warning"}
                variant={totalWeight === 100 ? "filled" : "outlined"}
                label={t("agentsListPage.skillParamsSdrFieldsWeightTotal", { total: totalWeight })}
              />
            </Stack>
            {totalWeight !== 100 && (
              <Alert severity="warning" sx={{ py: 0.75, mb: 1.5 }}>
                {t("agentsListPage.skillParamsSdrFieldsWeightHint")}
              </Alert>
            )}
            <Stack spacing={1.5}>
              {sdrFields.map((field, idx) => (
                <Box
                  key={`${field.key}-${idx}`}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
                    <Chip size="small" label={t("agentsListPage.skillParamsSdrFieldIndex", { index: idx + 1 })} />
                    {!readOnly && sdrFields.length > 1 ? (
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => removeSdrField(idx)}
                      >
                        {t("agentsListPage.skillParamsSdrFieldRemove")}
                      </Button>
                    ) : null}
                  </Stack>
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                      <TextField
                        label={t("agentsListPage.skillParamsSdrFieldLabel")}
                        value={field.label || ""}
                        onChange={(e) => {
                          const label = e.target.value;
                          const partial = { label };
                          if (!field.key || /^campo_\d+$/i.test(field.key)) {
                            partial.key = slugifySdrFieldKey(label, `campo_${idx + 1}`);
                          }
                          mergeSdrField(idx, partial);
                        }}
                        fullWidth
                        size="small"
                        disabled={readOnly}
                      />
                      <TextField
                        label={t("agentsListPage.skillParamsSdrFieldWeight")}
                        helperText={t("agentsListPage.skillParamsSdrFieldWeightHelper")}
                        value={field.weight ?? ""}
                        onChange={(e) => mergeSdrField(idx, { weight: e.target.value.replace(/\D/g, "") })}
                        fullWidth
                        size="small"
                        disabled={readOnly}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">pts</InputAdornment>,
                        }}
                        sx={{ maxWidth: { sm: 160 } }}
                      />
                    </Stack>
                    <TextField
                      label={t("agentsListPage.skillParamsSdrFieldQuestion")}
                      placeholder={t("agentsListPage.skillParamsSdrFieldQuestionPlaceholder")}
                      value={field.question || ""}
                      onChange={(e) => mergeSdrField(idx, { question: e.target.value })}
                      fullWidth
                      size="small"
                      disabled={readOnly}
                      multiline
                      minRows={2}
                    />
                    <FormControl size="small" fullWidth disabled={readOnly}>
                      <InputLabel id={`sdr-field-validation-${idx}`}>
                        {t("agentsListPage.skillParamsSdrFieldValidation", {
                          defaultValue: "Validação da resposta",
                        })}
                      </InputLabel>
                      <Select
                        labelId={`sdr-field-validation-${idx}`}
                        label={t("agentsListPage.skillParamsSdrFieldValidation", {
                          defaultValue: "Validação da resposta",
                        })}
                        value={field.validacao_resposta || VALIDACAO_RESPOSTA_NONE}
                        onChange={(e) => {
                          const next = e.target.value || VALIDACAO_RESPOSTA_NONE;
                          mergeSdrField(idx, {
                            validacao_resposta: next,
                            validacao_numero_min:
                              next === VALIDACAO_RESPOSTA_NUMERO_RANGE ? field.validacao_numero_min : null,
                            validacao_numero_max:
                              next === VALIDACAO_RESPOSTA_NUMERO_RANGE ? field.validacao_numero_max : null,
                          });
                        }}
                      >
                        {VALIDACAO_RESPOSTA_TYPES.map((type) => (
                          <MenuItem key={type || "none"} value={type}>
                            {t(
                              `chatbotConfigPage.chatbotInputValidation.types.${VALIDACAO_RESPOSTA_LABEL_KEYS[type] || "none"}`,
                              {
                                defaultValue:
                                  type === ""
                                    ? "Nenhuma (aceita qualquer texto)"
                                    : type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, " "),
                              }
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {t("agentsListPage.skillParamsSdrFieldValidationHelper", {
                          defaultValue:
                            "Com “Nenhuma”, o SDR aceita a resposta de forma aberta pelo contexto. Com um tipo selecionado, aplica as mesmas regras do chatbot.",
                        })}
                      </FormHelperText>
                    </FormControl>
                    {field.validacao_resposta === VALIDACAO_RESPOSTA_NUMERO_RANGE ? (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                        <TextField
                          label={t("chatbotConfigPage.chatbotInputValidation.rangeMin", {
                            defaultValue: "Mínimo",
                          })}
                          size="small"
                          fullWidth
                          value={field.validacao_numero_min ?? ""}
                          onChange={(e) =>
                            mergeSdrField(idx, {
                              validacao_numero_min: e.target.value.replace(/[^\d.,-]/g, ""),
                            })
                          }
                          disabled={readOnly}
                          placeholder="1"
                        />
                        <TextField
                          label={t("chatbotConfigPage.chatbotInputValidation.rangeMax", {
                            defaultValue: "Máximo",
                          })}
                          size="small"
                          fullWidth
                          value={field.validacao_numero_max ?? ""}
                          onChange={(e) =>
                            mergeSdrField(idx, {
                              validacao_numero_max: e.target.value.replace(/[^\d.,-]/g, ""),
                            })
                          }
                          disabled={readOnly}
                          placeholder="10"
                        />
                      </Stack>
                    ) : null}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.required !== false}
                          onChange={(e) => mergeSdrField(idx, { required: e.target.checked })}
                          disabled={readOnly}
                        />
                      }
                      label={t("agentsListPage.skillParamsSdrFieldRequired")}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
            {!readOnly && (
              <Button startIcon={<AddIcon />} onClick={addSdrField} sx={{ mt: 1.5 }}>
                {t("agentsListPage.skillParamsSdrFieldAdd")}
              </Button>
            )}
          </SubSection>
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={t("agentsListPage.skillParamsSdrAgendaTitle")}
        subtitle={formCopy("accordionSdrAgendaSubtitle", "agentsListPage.skillParamsSdrAgendaHint")}
      >
        <Stack spacing={2.5}>
          <SubSection
            icon={CalendarMonthOutlinedIcon}
            title={t("agentsListPage.skillParamsSdrMeetingSettingsTitle")}
            description={t("agentsListPage.skillParamsSdrMeetingSettingsHint")}
          >
            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={meetingEnabled}
                    onChange={(e) => mergeSdrConfig({ meeting_enabled: e.target.checked })}
                    disabled={readOnly}
                  />
                }
                label={t("agentsListPage.skillParamsSdrMeetingEnabled")}
              />
              <Collapse in={meetingEnabled}>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={skillConfig.require_email_for_meeting !== false}
                        onChange={(e) => mergeSdrConfig({ require_email_for_meeting: e.target.checked })}
                        disabled={readOnly}
                      />
                    }
                    label={t("agentsListPage.skillParamsSdrRequireEmail")}
                  />
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    filterSelectedOptions
                    options={sdrSellerOptions}
                    value={selectedSdrSellers}
                    loading={sdrUsersLoading}
                    disabled={readOnly}
                    getOptionLabel={userDisplayLabel}
                    isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                    onChange={(_, next) =>
                      mergeSdrConfig({ seller_user_ids: next.map((user) => Number(user.id)).filter(Boolean) })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("agentsListPage.skillParamsSdrSellerUserIds")}
                        helperText={
                          sdrUsersLoading
                            ? t("agentsListPage.skillParamsSdrSellerUserIdsLoading")
                            : t("agentsListPage.skillParamsSdrSellerUserIdsHelper")
                        }
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {sdrUsersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {sdrUsersError && (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      {t("agentsListPage.skillParamsSdrSellerUserIdsLoadError")}
                    </Alert>
                  )}
                  {meetingEnabled && !sdrSellerIds.length && !sdrUsersLoading && (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      {t("agentsListPage.skillParamsSdrSellerRequired")}
                    </Alert>
                  )}
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    filterSelectedOptions
                    options={sdrCalendarOptions}
                    value={selectedSdrCalendars}
                    loading={agendaCalendarsLoading}
                    disabled={readOnly}
                    getOptionLabel={(cal) => calendarDisplayLabel(cal, sdrUsersById)}
                    isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                    onChange={(_, next) =>
                      mergeSdrConfig({ calendar_ids: next.map((cal) => Number(cal.id)).filter(Boolean) })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("agentsListPage.skillParamsSdrCalendarIds")}
                        helperText={
                          agendaCalendarsLoading
                            ? t("agentsListPage.skillParamsSdrCalendarIdsLoading")
                            : t("agentsListPage.skillParamsSdrCalendarIdsHelper")
                        }
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {agendaCalendarsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {agendaCalendarsError && (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      {t("agentsListPage.skillParamsSdrCalendarIdsLoadError")}
                    </Alert>
                  )}
                  <TextField
                    label={t("agentsListPage.skillParamsSdrMeetingLocation")}
                    placeholder={t("agentsListPage.skillParamsSdrMeetingLocationPlaceholder")}
                    value={skillConfig.meeting_location || "Online"}
                    onChange={(e) => mergeSdrConfig({ meeting_location: e.target.value })}
                    fullWidth
                    size="small"
                    disabled={readOnly}
                  />
                </Stack>
              </Collapse>
              {!meetingEnabled && (
                <Alert severity="info" sx={{ py: 0.75 }}>
                  {t("agentsListPage.skillParamsSdrMeetingDisabledHint")}
                </Alert>
              )}
            </Stack>
          </SubSection>

          <Collapse in={meetingEnabled}>
            <SubSection
              title={t("agentsListPage.skillParamsSdrAvailabilityTitle")}
              description={t("agentsListPage.skillParamsSdrAvailabilityHint")}
            >
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label={t("agentsListPage.skillParamsSdrDuration")}
                    helperText={t("agentsListPage.skillParamsSdrDurationHelper")}
                    value={skillConfig.meeting_duration_minutes || 30}
                    onChange={(e) =>
                      mergeSdrConfig({ meeting_duration_minutes: e.target.value.replace(/\D/g, "") })
                    }
                    fullWidth
                    size="small"
                    disabled={readOnly}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">min</InputAdornment>,
                    }}
                  />
                  <TextField
                    label={t("agentsListPage.skillParamsSdrWindowDays")}
                    helperText={t("agentsListPage.skillParamsSdrWindowDaysHelper")}
                    value={skillConfig.scheduling_window_days || 14}
                    onChange={(e) =>
                      mergeSdrConfig({ scheduling_window_days: e.target.value.replace(/\D/g, "") })
                    }
                    fullWidth
                    size="small"
                    disabled={readOnly}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{t("agentsListPage.skillParamsSdrDaysSuffix")}</InputAdornment>,
                    }}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label={t("agentsListPage.skillParamsSdrBusinessStart")}
                    type="time"
                    value={toTimeInputValue(skillConfig.business_hours?.start || "09:00")}
                    onChange={(e) => mergeSdrBusinessHours({ start: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    disabled={readOnly}
                  />
                  <TextField
                    label={t("agentsListPage.skillParamsSdrBusinessEnd")}
                    type="time"
                    value={toTimeInputValue(skillConfig.business_hours?.end || "18:00")}
                    onChange={(e) => mergeSdrBusinessHours({ end: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                    disabled={readOnly}
                  />
                </Stack>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                    {t("agentsListPage.skillParamsSdrBusinessWeekdays")}
                  </Typography>
                  <FormGroup row>
                    {FAQ_WEEKDAYS.map(({ value, labelKey }) => (
                      <FormControlLabel
                        key={value}
                        control={
                          <Checkbox
                            size="small"
                            checked={sdrBusinessWeekdays.includes(value)}
                            onChange={() => toggleSdrWeekday(value)}
                            disabled={readOnly}
                          />
                        }
                        label={t(labelKey)}
                      />
                    ))}
                  </FormGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    {t("agentsListPage.skillParamsSdrBusinessWeekdaysHelper")}
                  </Typography>
                </Box>
              </Stack>
            </SubSection>
          </Collapse>
        </Stack>
      </ConfigAccordion>

      <ConfigAccordion
        title={t("agentsListPage.skillParamsSdrMessagesTitle")}
        subtitle={formCopy("accordionSdrMessagesSubtitle", "agentsListPage.skillParamsSdrMessagesHint")}
      >
        <Stack spacing={2.5}>
          <Alert severity="info" icon={<ChatBubbleOutlineOutlinedIcon fontSize="inherit" />} sx={{ py: 0.75 }}>
            {t("agentsListPage.skillParamsSdrMessagesInfo")}
          </Alert>
          <TextField
            label={t("agentsListPage.skillParamsSdrIntroMessage")}
            placeholder={SDR_DEFAULT_MESSAGES.intro_message}
            value={skillConfig.intro_message || ""}
            onChange={(e) => mergeSdrConfig({ intro_message: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label={t("agentsListPage.skillParamsSdrQualifiedMessage")}
            placeholder={SDR_DEFAULT_MESSAGES.qualified_message}
            value={skillConfig.qualified_message || ""}
            onChange={(e) => mergeSdrConfig({ qualified_message: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label={t("agentsListPage.skillParamsSdrDisqualifiedMessage")}
            placeholder={SDR_DEFAULT_MESSAGES.disqualified_message}
            value={skillConfig.disqualified_message || ""}
            onChange={(e) => mergeSdrConfig({ disqualified_message: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label={t("agentsListPage.skillParamsSdrNoSlotsMessage")}
            placeholder={SDR_DEFAULT_MESSAGES.no_slots_message}
            value={skillConfig.no_slots_message || ""}
            onChange={(e) => mergeSdrConfig({ no_slots_message: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label={t("agentsListPage.skillParamsSdrEmailQuestion")}
            placeholder={SDR_DEFAULT_MESSAGES.email_question}
            value={skillConfig.email_question || ""}
            onChange={(e) => mergeSdrConfig({ email_question: e.target.value })}
            fullWidth
            size="small"
            disabled={readOnly}
          />
        </Stack>
      </ConfigAccordion>
    </Stack>
  );
}
